// Landing View - Welcome to Cigar Advisor
import React, { useEffect, useRef, useState } from 'react';
import { Check, User, Download, Share2, Smartphone } from 'lucide-react';
import { CigarIcon } from '../components/Icons';

// Google One Tap Client ID
const GOOGLE_CLIENT_ID = '687509338462-l3brgs5s48h360g4vnt85avg6b05i3jd.apps.googleusercontent.com';

// Google Identity Services types (separate from Google Maps API)
interface GoogleIdentityServices {
  accounts: {
    id: {
      initialize: (config: {
        client_id: string;
        callback: (response: { credential: string }) => void;
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
      }) => void;
      renderButton: (
        element: HTMLElement,
        options: {
          theme?: 'outline' | 'filled_blue' | 'filled_black';
          size?: 'large' | 'medium' | 'small';
          type?: 'standard' | 'icon';
          text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
          shape?: 'rectangular' | 'pill' | 'circle' | 'square';
          logo_alignment?: 'left' | 'center';
          width?: number;
        }
      ) => void;
      prompt: () => void;
    };
  };
}

// Helper to get Google Identity Services from window
const getGoogleIdentity = (): GoogleIdentityServices | null => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const win = window as any;
  return win.google?.accounts ? win.google as GoogleIdentityServices : null;
};

interface LandingViewProps {
  onLogin: (email: string, password: string) => Promise<string | null>;
  onRegister: (email: string, password: string, name: string) => Promise<string | null>;
  onGoogleLogin: (credential: string) => Promise<string | null>;
  onGuest: () => void;
}

export const LandingView: React.FC<LandingViewProps> = ({ onLogin, onRegister, onGoogleLogin, onGuest }) => {
  const [mode, setMode] = React.useState<'welcome' | 'login' | 'register'>('welcome');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [name, setName] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);

  // Load Google Identity Services script and initialize
  useEffect(() => {
    // Check if script is already loaded
    const gsi = getGoogleIdentity();
    if (gsi) {
      initializeGoogle();
      return;
    }

    // Load the script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initializeGoogle;
    document.head.appendChild(script);

    return () => {
      // Cleanup not needed as we want the script to persist
    };
  }, []);

  // Re-render button when mode changes to welcome
  useEffect(() => {
    const gsi = getGoogleIdentity();
    if (mode === 'welcome' && gsi && googleButtonRef.current) {
      renderGoogleButton();
    }
  }, [mode]);

  const initializeGoogle = () => {
    const gsi = getGoogleIdentity();
    if (!gsi) return;

    gsi.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleCallback,
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    renderGoogleButton();
  };

  const renderGoogleButton = () => {
    const gsi = getGoogleIdentity();
    if (!gsi || !googleButtonRef.current) return;

    // Clear existing button
    googleButtonRef.current.innerHTML = '';

    gsi.accounts.id.renderButton(googleButtonRef.current, {
      theme: 'filled_black',
      size: 'large',
      type: 'standard',
      text: 'continue_with',
      shape: 'pill',
      width: 300,
    });
  };

  const handleGoogleCallback = async (response: { credential: string }) => {
    setError('');
    setLoading(true);
    const err = await onGoogleLogin(response.credential);
    if (err) setError(err);
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (mode === 'login') {
      const err = await onLogin(email, password);
      if (err) setError(err);
    } else if (mode === 'register') {
      const err = await onRegister(email, password, name);
      if (err) setError(err);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-stone-900 relative overflow-hidden">
      {/* Background image with overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-40"
        style={{
          backgroundImage: `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 800"><rect fill="%231c1917"/><circle cx="600" cy="400" r="300" fill="%2378350f" opacity="0.3"/><circle cx="200" cy="600" r="200" fill="%2392400e" opacity="0.2"/></svg>')`,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-stone-900/80 via-stone-900/60 to-stone-900" />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-2">
          <CigarIcon className="w-7 h-7 text-amber-500" />
          <span className="text-lg font-bold text-white">Cigar Advisor</span>
        </div>
        <button
          onClick={() => setMode('login')}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/20 text-amber-500 hover:bg-amber-500/30 transition-colors"
        >
          <User className="w-4 h-4" />
          <span className="text-sm font-medium">About</span>
        </button>
      </header>

      {/* Main content */}
      <main className="relative z-10 flex flex-col items-center justify-center px-6 py-12 min-h-[calc(100vh-80px)]">
        {/* Hero section */}
        <div className="text-center mb-8 max-w-2xl">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            <span className="italic">Welcome to</span>{' '}
            <span className="text-amber-500 italic">Cigar Advisor</span>
          </h1>
          <p className="text-white/70 text-lg">
            Your personal guide to cigar festivals. Discover, taste, and remember.
          </p>
        </div>

        {/* Feature card */}
        <div className="w-full max-w-md bg-stone-800/80 backdrop-blur-lg rounded-2xl border border-white/10 p-6 mb-8">
          <h2 className="text-xl font-bold text-white mb-4 text-center">
            Register to Enhance Your Festival Experience
          </h2>

          <div className="space-y-3 mb-6">
            <FeatureItem text="Save your cigar preferences and tasting notes" />
            <FeatureItem text="Access past recommendations anytime" />
            <FeatureItem text="Get a personalized guide for the festival" />
          </div>

          {mode === 'welcome' ? (
            <div className="space-y-3">
              {/* Google Sign-in Button */}
              <div className="flex justify-center">
                <div ref={googleButtonRef} className="google-signin-button" />
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-white/20" />
                <span className="text-white/40 text-sm">or</span>
                <div className="flex-1 h-px bg-white/20" />
              </div>

              <button
                onClick={() => setMode('register')}
                className="w-full py-3 px-4 rounded-xl bg-amber-500 text-stone-900 font-semibold hover:bg-amber-400 transition-colors"
              >
                Register with Email
              </button>
              <button
                onClick={() => setMode('login')}
                className="w-full py-3 px-4 rounded-xl bg-white/10 text-white font-semibold hover:bg-white/20 transition-colors border border-white/20"
              >
                Log In with Email
              </button>

              {error && (
                <p className="text-red-400 text-sm text-center">{error}</p>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'register' && (
                <input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-stone-700 border border-white/10 text-white placeholder:text-white/40 focus:outline-none focus:border-amber-500"
                  required
                />
              )}
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-stone-700 border border-white/10 text-white placeholder:text-white/40 focus:outline-none focus:border-amber-500"
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-stone-700 border border-white/10 text-white placeholder:text-white/40 focus:outline-none focus:border-amber-500"
                required
              />

              {error && (
                <p className="text-red-400 text-sm text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 rounded-xl bg-amber-500 text-stone-900 font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50"
              >
                {loading ? 'Please wait...' : mode === 'login' ? 'Log In' : 'Create Account'}
              </button>

              <button
                type="button"
                onClick={() => setMode('welcome')}
                className="w-full py-2 text-white/60 hover:text-white text-sm"
              >
                Back
              </button>
            </form>
          )}
        </div>

        {/* Guest option */}
        <button
          onClick={onGuest}
          className="text-white/60 hover:text-white border border-white/20 rounded-full px-8 py-3 hover:bg-white/5 transition-colors"
        >
          Continue as Guest
        </button>

        {/* Festival badges */}
        <div className="mt-12 flex flex-wrap justify-center gap-4">
          <FestivalBadge name="Ybor Cigar Fest" />
          <FestivalBadge name="PCA Trade Show" />
          <FestivalBadge name="Denver Festival" />
        </div>
      </main>

      {/* Footer with PWA Install & Share */}
      <footer className="relative z-10 border-t border-white/10 bg-stone-900/80 backdrop-blur-sm">
        <div className="max-w-md mx-auto px-6 py-6">
          <div className="flex justify-center gap-6">
            <InstallButton />
            <ShareButton />
          </div>
          <p className="text-center text-white/30 text-xs mt-4">
            Ring & Ash v1.0 · Your Cigar Companion
          </p>
        </div>
      </footer>
    </div>
  );
};

const FeatureItem: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex items-start gap-3">
    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center mt-0.5">
      <Check className="w-3 h-3 text-amber-500" />
    </div>
    <span className="text-white/80 text-sm">{text}</span>
  </div>
);

const FestivalBadge: React.FC<{ name: string }> = ({ name }) => (
  <div className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm">
    {name}
  </div>
);

// PWA Install Button Component
const InstallButton: React.FC = () => {
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as any).standalone === true;
    setIsInstalled(isStandalone);

    // Detect iOS
    const ua = window.navigator.userAgent.toLowerCase();
    setIsIOS(/iphone|ipad|ipod/.test(ua) && !(window as any).MSStream);

    // Listen for install prompt
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSModal(true);
      return;
    }
    if (installPrompt) {
      await installPrompt.prompt();
    }
  };

  if (isInstalled) {
    return (
      <div className="flex items-center gap-2 text-green-400 text-sm">
        <Smartphone className="w-4 h-4" />
        <span>Installed</span>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={handleInstall}
        className="flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/20 text-amber-500 hover:bg-amber-500/30 transition-colors text-sm font-medium"
      >
        <Download className="w-4 h-4" />
        <span>Install App</span>
      </button>

      {/* iOS Instructions Modal */}
      {showIOSModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowIOSModal(false)} />
          <div className="relative bg-stone-800 rounded-2xl p-6 max-w-sm w-full border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-4">Install on iOS</h3>
            <div className="space-y-3 text-white/70 text-sm">
              <p>1. Tap the <strong className="text-white">Share</strong> button in Safari</p>
              <p>2. Scroll down and tap <strong className="text-white">"Add to Home Screen"</strong></p>
              <p>3. Tap <strong className="text-white">"Add"</strong> to install</p>
            </div>
            <button
              onClick={() => setShowIOSModal(false)}
              className="w-full mt-6 py-2.5 bg-amber-500 text-stone-900 font-medium rounded-xl hover:bg-amber-400"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// Share Button Component
const ShareButton: React.FC = () => {
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const shareData = {
      title: 'Ring & Ash - Cigar Companion',
      text: 'Your personal cigar advisor, journal, and lounge finder.',
      url: 'https://ringandash.com',
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      // User cancelled or error
    }
  };

  return (
    <button
      onClick={handleShare}
      className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition-colors text-sm font-medium"
    >
      <Share2 className="w-4 h-4" />
      <span>{copied ? 'Copied!' : 'Share'}</span>
    </button>
  );
};

export default LandingView;
